import { gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import { APPLICATION_AIDETECTION_FRAGMENT } from '../ApplicationFragments';

type Job = {
  title: string;
  threshold: number;
  pinned: boolean;
};

type Leads = {
  totalCount: number;
  nodes: {
    id: string;
    stage: number;
    score: number;
    status: string;
    accepted: boolean;
    createdAt: string;
    candidate: {
      id: string;
      firstName: string;
      lastName: string;
    };
    aiDetection: {
      isAi: boolean;
    };
    activityLog: {
      createdAt: string;
      actionType: string;
      actionSubType: string;
      message: string;
    }[];
  }[];
};

export type KanbanLeads = {
  hubertLeads: Leads;
  acceptedLeads: Leads;
  hired: Leads;
  notHired: Leads;
  newApplicants: Leads;
  [key: string]: Leads;
};

const useKanban = (args: { jobId?: string; initLimit: number; showPassed?: boolean; sort: string }) => {
  const { initLimit, jobId } = args;
  const showPassed = args.showPassed === undefined ? true : !!args.showPassed;
  const [limit] = useState(initLimit ? initLimit : 25);

  const [counter, setCounter] = useState([
    { id: 'newApplicants', title: 'New Applicants', disabled: false, skip: limit, skipName: 'newApplicantsSkip' },
    { id: 'hubertLeads', title: 'New Leads', disabled: false, skip: limit, skipName: 'hubertLeadsSkip' },
    { id: 'acceptedLeads', title: 'Accepted Leads', disabled: false, skip: limit, skipName: 'acceptedLeadsSkip' },
    { id: 'hired', title: 'Hired', disabled: false, skip: limit, skipName: 'hiredSkip' },
    { id: 'notHired', title: 'Not Hired', disabled: false, skip: limit, skipName: 'notHiredSkip' },
  ]);

  const KANBAN_FRAGMENT = gql`
    fragment Kanban_application on Application {
      id
      candidate {
        id
        firstName
        lastName
        email
      }
      status
      score
      stage
      createdAt
      accepted
      activityLog {
        createdAt
        actionType
        actionSubType
        message
      }
      ...Application_aiDetection
      documents {
        id
        type
      }
    }
    ${APPLICATION_AIDETECTION_FRAGMENT}
  `;

  const NEW_LEADS = gql`
  query NEW_LEADS(
    $jobId: String
    $limit: Int
    $hubertLeadsSkip: Int
    $acceptedLeadsSkip: Int
    $hiredSkip: Int
    $newApplicantsSkip: Int
    $notHiredSkip: Int
    $sort: ApplicationSortableFields!
  ) {
    job(jobId: $jobId) {
      id
      title
      company
      pinned
      client {
        id
      }
      location {
        id
        name
        address
      }
      threshold
      integration {
        teamtailor {
          jobId
          companyId
        }
      }
      collaborators {
        users {
          user {
            id
            firstName
            lastName
            email
            flag
            client {
              orgName
            }
          }
        }
        accessGroups {
          accessGroup {
            id
            name
            description
            members {
              id
              firstName
              lastName
              flag
              email
            }
            jobs {
              job {
                id
                title
              }
            }
          }
        }
      }
      applications {
        totalCount
      }
      newApplicants: applications(
        filter: { status: null, stage: { equals: null } }
        sort: { field: createdAt, order: DESC }
        limit: $limit
        skip: $newApplicantsSkip
      ) {
        totalCount
        nodes {
          ...Kanban_application
        }
      }
      hubertLeads: applications(
        filter: { stage: { equals: 2 },${showPassed ? '' : ' scoreGteThreshold: false'}}
        sort: { field: $sort, order: DESC }
        limit: $limit
        skip: $hubertLeadsSkip
      ) {
        totalCount
        nodes {
          ...Kanban_application
        }
      }
      acceptedLeads: applications(
        filter: { stage: { equals: 3 } }
        sort: { field: $sort, order: DESC }
        limit: $limit
        skip: $acceptedLeadsSkip
      ) {
        totalCount
        nodes {
          ...Kanban_application
        }
      }
      hired: applications(
        filter: { stage: { equals: 4 } }
        sort: { field: $sort, order: DESC }
        limit: $limit
        skip: $hiredSkip
      ) {
        totalCount
        nodes {
          ...Kanban_application
        }
      }
      notHired: applications(
        filter: { stage: { equals: 5 } }
        sort: { field: $sort, order: DESC }
        limit: $limit
        skip: $notHiredSkip
      ) {
        totalCount
        nodes {
          ...Kanban_application
        }
      }
    }
  }
  ${KANBAN_FRAGMENT}
`;
  const { data, error, fetchMore, loading, refetch } = useQuery<{ job: Job & KanbanLeads }>(NEW_LEADS, {
    variables: { jobId: jobId, limit: limit, sort: args.sort },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted(data) {
      setCounter((c) => {
        const newCount = [...c];
        newCount.forEach((x) => {
          x.disabled = data.job[x.id].totalCount <= data.job[x.id].nodes.length;
        });
        return newCount;
      });
    },
  });

  const refetching = () => {
    refetch({});
  };
  const getMore = (id: string) => {
    const obj = counter.find((x) => x.id === id);
    if (!obj) return;

    const vars = {
      [obj.skipName]: obj.skip,
      limit: limit,
    };

    fetchMore({
      variables: vars,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const ret = {
          ...previousResult,
          job: {
            ...previousResult.job,
            [obj.id]: {
              ...fetchMoreResult.job[obj.id],
              nodes: [...previousResult.job[obj.id].nodes, ...fetchMoreResult.job[obj.id].nodes].filter(
                (job, i, self) => i === self.findIndex((t) => t.id === job.id),
              ),
            },
          },
        };
        setCounter((c) => {
          const tempCounter = [...c];
          const index = tempCounter.findIndex((x) => x.id === obj.id);
          tempCounter[index] = {
            ...tempCounter[index],
            skip: ret.job[obj.id].nodes.length,
            disabled: ret.job[obj.id].nodes.length >= ret.job[obj.id].totalCount,
          };
          return tempCounter;
        });
        return ret;
      },
    });
  };

  const temp = {
    hubertLeads: {
      totalCount: 0,
      nodes: [],
    },
    acceptedLeads: {
      totalCount: 0,
      nodes: [],
    },
    hired: {
      totalCount: 0,
      nodes: [],
    },
    notHired: {
      totalCount: 0,
      nodes: [],
    },
    newApplicants: {
      totalCount: 0,
      nodes: [],
    },
  };

  const hubertLeads = data && data.job.hubertLeads ? data.job.hubertLeads : temp.hubertLeads;
  const acceptedLeads = data && data.job.acceptedLeads ? data.job.acceptedLeads : temp.acceptedLeads;
  const hired = data && data.job.hired ? data.job.hired : temp.hired;
  const notHired = data && data.job.notHired ? data.job.notHired : temp.notHired;
  const newApplicants = data && data.job.newApplicants ? data.job.newApplicants : temp.newApplicants;
  const job = {
    title: data && data.job.title ? data.job.title : '',
    threshold: data && data.job.threshold ? data.job.threshold : 0,
    pinned: data && data.job ? data.job.pinned : false,
  };

  const disabled = Object.fromEntries(counter.map((x) => [x.id, x.disabled]));

  return {
    job,
    hubertLeads,
    acceptedLeads,
    hired,
    notHired,
    newApplicants,
    error,
    getMore,
    disabled,
    loading,
    refetching,
  };
};

export default useKanban;
